/* What is promised, to whom, and where each signature stands — as the redesign's contract cards.
 *
 * **A pending contract is not incomplete — it is waiting on a named party**, and the two signature
 * slots on each card say which. Never a dash: "who has not signed" is the question those slots
 * answer, and a dash answers it with silence. Where a side is still owed, the slot offers Sign, so
 * the person who is waited on can act where they read it.
 *
 * **A breach is shown where the contract is, from the incident it opened.** The card reads the open
 * incident queue for a `contract_breach` whose subject is this contract, rather than inferring a
 * breach from anything the contract row carries — the row carries no verdict, and an inferred one
 * would be a claim nothing measured.
 *
 * **The state filter runs over the fetched page, and the page says when it is bounded.** The route
 * accepts `?state=`, and sending it would give the cards a different source of truth from the
 * filter counts — two answers to "how many drafts are there". One fetch, one answer, and `truncated`
 * is what keeps "no contract in this state" from being a claim made at a page boundary.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { useIncidentQueue } from '@/features/incidents';
import {
  CREATE_CONTRACT_PERMISSION,
  EVALUATE_CONTRACT_PERMISSION,
  READ_INCIDENTS_PERMISSION,
  SIGN_CONTRACT_PERMISSION,
  SUBMIT_CONTRACT_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import type { Contract, SignoffSide } from '@/shared/api/generated/contracts';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';

import { useContracts, useEvaluateContract, useSignContract } from '../api/contracts';
import {
  matchesFilter,
  SEVERITY_TONE,
  signoffsOf,
  stateLabelKey,
  STATE_FILTERS,
  toneOf,
} from '../model/contracts';
import type { StateFilter } from '../model/contracts';
import { NewContractDrawer } from './NewContractDrawer';

interface CardProps {
  readonly contract: Contract;
  readonly datasetName: string | undefined;
  /** The open `contract_breach` incident about this contract, if any. */
  readonly breach: string | undefined;
  readonly maySign: boolean;
  readonly signing: SignoffSide | null;
  readonly signError: string | undefined;
  readonly onSign: (side: SignoffSide) => void;
}

/** Which sides are still owed, as the design says it: one sentence for both, one per side. */
function missingKey(contract: Contract): string | null {
  const owed = new Set(contract.unsigned_sides);
  if (contract.state !== 'pending_signoff' || owed.size === 0) return null;
  if (owed.size === 2) return 'contracts.card.missingBoth';
  return owed.has('producer')
    ? 'contracts.card.missingProducer'
    : 'contracts.card.missingConsumer';
}

/* One promise as the design's card: who it binds and on which dataset, its state, what it promises,
 * the two signatures, and a breach when one is open. The whole card opens the contract; the Open
 * button is the keyboard route, because an `article` with an `onClick` is not a control. */
function ContractCard({
  contract,
  datasetName,
  breach,
  maySign,
  signing,
  signError,
  onSign,
}: CardProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = (): void => {
    navigate(`/contracts/${encodeURIComponent(contract.id)}`);
  };
  const terms = contract.terms;
  const missing = missingKey(contract);
  const now = new Date();

  return (
    <article
      className="lq-ct__card"
      data-breached={breach === undefined ? undefined : 'true'}
      onClick={open}
    >
      <div className="lq-ct__cardHead">
        <div className="lq-ct__cardTitle">
          <strong className="lq-ct__name">{contract.name}</strong>
          <div className="lq-ct__cardParties">
            {t('contracts.card.parties', {
              producer: contract.producer_subject,
              consumer: contract.consumer_subject,
            })}
            {' · '}
            {/* A dataset by its name, never its id; one the catalogue listing did not reach says so
                rather than printing a UUID in the line a reader scans first. */}
            <span className="lq-mono" title={contract.dataset_id}>
              {datasetName ?? t('contracts.card.datasetUnknown')}
            </span>
          </div>
        </div>
        <span
          className={`lq-tag lq-tag--ct-${breach === undefined ? toneOf(contract.state) : 'breached'}`}
          title={t(`contracts.stateHint.${contract.state}`)}
        >
          <span className="lq-tag__dot" aria-hidden="true" />
          {breach === undefined
            ? t(stateLabelKey(contract.state))
            : t('contracts.card.breachedState')}
        </span>
      </div>

      {/* What is promised, in sentences. A contract with no terms cannot exist — the server refuses
          it — so this list is never empty. */}
      <ul className="lq-ct__cardTerms">
        {terms.min_quality_score !== null && (
          <li>{t('contracts.card.term.score', { value: terms.min_quality_score })}</li>
        )}
        {terms.max_assessment_age_seconds !== null && (
          <li>
            {t('contracts.card.term.assessment', {
              hours: Math.round(terms.max_assessment_age_seconds / 3600),
            })}
          </li>
        )}
        {terms.pinned_schema_version !== null && (
          <li>
            {t('contracts.card.term.schema', { version: terms.pinned_schema_version })}
          </li>
        )}
      </ul>

      <div className="lq-ct__slots">
        {signoffsOf(contract).map((signoff) => {
          const owed = signoff.signedBy === null;
          const side = t(`contracts.column.${signoff.side}`);
          return (
            <div key={signoff.side} className="lq-ct__slot">
              <div className="lq-ct__slotSide">{side}</div>
              <div className="lq-ct__slotWho">
                <span
                  aria-hidden="true"
                  className={owed ? 'lq-ct__mark' : 'lq-ct__mark lq-ct__mark--ok'}
                >
                  {owed ? '○' : '✓'}
                </span>
                <span className={owed ? 'lq-ct__unsigned' : undefined}>
                  {signoff.signedBy === null
                    ? t('contracts.slot.notSigned')
                    : personName(signoff.signedBy)}
                </span>
              </div>
              <div className="lq-ct__slotWhen">
                {signoff.signedAt === null ? (
                  /* Who it is waiting for, by the declared party — the one subject the platform
                     accepts a signature from on this side. */
                  t('contracts.slot.waitingFor', { party: signoff.subject })
                ) : (
                  <span title={signoff.signedAt}>
                    {t('contracts.slot.signedAgo', {
                      age: ageText(t, signoff.signedAt, now) ?? signoff.signedAt,
                    })}
                  </span>
                )}
              </div>
              {owed && contract.state === 'pending_signoff' ? (
                <button
                  type="button"
                  className="lq-ct__sign"
                  disabled={!maySign || signing !== null}
                  /* "Sign" alone repeats on every owed slot, so the side is part of the name a
                     screen reader announces; the title says why it is disabled when it is. */
                  aria-label={t('contracts.slot.signAs', { side: side.toLowerCase() })}
                  title={
                    maySign
                      ? t('contracts.slot.signAs', { side: side.toLowerCase() })
                      : t('contracts.slot.signNotPermitted')
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    onSign(signoff.side);
                  }}
                >
                  {signing === signoff.side
                    ? t('contracts.slot.signing')
                    : t('contracts.slot.sign')}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {signError !== undefined ? (
        <p className="lq-ct__signError" role="alert">
          {signError}
        </p>
      ) : null}

      {missing !== null ? <div className="lq-ct__missing">{t(missing)}</div> : null}

      {breach !== undefined ? (
        <div className="lq-ct__breachBanner">
          <span className="lq-ct__breachText">{t('contracts.card.breached')}</span>
          <button
            type="button"
            className="lq-ct__breachOpen"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/incidents/${encodeURIComponent(breach)}`);
            }}
          >
            {t('contracts.card.openIncident')}
          </button>
        </div>
      ) : null}

      <div className="lq-ct__cardFoot">
        <span className="lq-ct__cardBreach">
          {t('contracts.card.onBreach')}{' '}
          <span
            className={`lq-ct__sevText lq-ct__sevText--${SEVERITY_TONE[contract.breach_severity]}`}
          >
            {t(`contracts.severity.${contract.breach_severity}`)}
          </span>
        </span>
        <button
          type="button"
          className="lq-rowaction"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t('contracts.open')}
        </button>
      </div>
    </article>
  );
}

export function ContractsLedger(): ReactElement {
  const { t } = useTranslation();
  const contracts = useContracts();
  const identity = useIdentity();
  const evaluate = useEvaluateContract();
  const sign = useSignContract();
  const [filter, setFilter] = useState<StateFilter>('all');
  const [search, setSearch] = useState('');
  const datasetNames = useDatasetNames();
  const [authoring, setAuthoring] = useState(false);
  const [signing, setSigning] = useState<{
    readonly id: string;
    readonly side: SignoffSide;
  } | null>(null);
  const [signError, setSignError] = useState<{
    readonly id: string;
    readonly message: string;
  } | null>(null);
  const [message, setMessage] = useState<{
    readonly titleKey: string;
    readonly bodyKey: string;
    readonly values: Record<string, string | number>;
  } | null>(null);

  const mayCreate = can(identity.data, CREATE_CONTRACT_PERMISSION);
  const mayEvaluate = can(identity.data, EVALUATE_CONTRACT_PERMISSION);
  const maySign = can(identity.data, SIGN_CONTRACT_PERMISSION);
  const maySubmit = can(identity.data, SUBMIT_CONTRACT_PERMISSION);
  const mayReadIncidents = can(identity.data, READ_INCIDENTS_PERMISSION);
  /* Only with the permission to read incidents: a refused read would otherwise sit on this screen as
     a failure that has nothing to do with contracts. */
  const incidents = useIncidentQueue({ enabled: mayReadIncidents });
  const breaches = new Map<string, string>(
    (incidents.data ?? [])
      .filter((incident) => incident.kind === 'contract_breach' && incident.active)
      .map((incident) => [incident.subject_id, incident.id]),
  );

  const all = contracts.data?.items ?? [];
  const truncated = contracts.data?.truncated === true;
  const needle = search.trim().toLowerCase();
  const visible = all.filter(
    (contract) =>
      matchesFilter(contract, filter) &&
      (needle === '' ||
        [
          contract.name,
          contract.producer_subject,
          contract.consumer_subject,
          datasetNames.data?.byId.get(contract.dataset_id) ?? '',
        ].some((field) => field.toLowerCase().includes(needle))),
  );
  const active = all.filter((contract) => contract.state === 'active');
  const readOnly =
    identity.isSuccess && !mayCreate && !maySubmit && !maySign && !mayEvaluate;

  const signFor = (contract: Contract, side: SignoffSide): void => {
    setSignError(null);
    setMessage(null);
    setSigning({ id: contract.id, side });
    sign.mutate(
      { id: contract.id, side, reason: t('contracts.reason.sign') },
      {
        onSuccess: () => {
          setMessage({
            titleKey: 'contracts.acted.signedTitle',
            bodyKey: `contracts.acted.signed.${side}`,
            values: { name: contract.name },
          });
        },
        /* The server's own sentence, on the card it is about: a signature from anyone but the
           declared party is refused, and that refusal names who may sign. */
        onError: (failure) => {
          setSignError({ id: contract.id, message: failure.message });
        },
        onSettled: () => {
          setSigning(null);
        },
      },
    );
  };

  /* One request per active contract, because there is no bulk route. Bounded by how many contracts
   * a tenant has active, which is small — and sequential rather than parallel, so a tenant with
   * thirty does not open thirty connections to its own API at once. */
  const evaluateAll = (): void => {
    setMessage(null);
    void active
      .reduce<Promise<number>>(
        (chain, contract) =>
          chain.then(async (broken) => {
            const outcome = await evaluate.mutateAsync(contract.id);
            return broken + (outcome.held ? 0 : 1);
          }),
        Promise.resolve(0),
      )
      .then((broken) => {
        setMessage({
          titleKey: 'contracts.acted.evaluatedAll',
          bodyKey: broken === 0 ? 'contracts.acted.allHeld' : 'contracts.acted.someBroke',
          values: { count: active.length, broken },
        });
      })
      .catch((failure: unknown) => {
        setMessage({
          titleKey: 'contracts.acted.evaluateFailed',
          bodyKey: 'contracts.acted.evaluateFailedBody',
          values: {
            detail: failure instanceof Error ? failure.message : String(failure),
          },
        });
      });
  };

  return (
    <div className="lq-ct">
      <PageHeader
        title={t('contracts.title')}
        help={t('help.feature.contracts')}
        lede={t('contracts.intro')}
        actions={
          contracts.isSuccess && mayCreate ? (
            <button
              type="button"
              className="lq-ct__act lq-ct__act--primary"
              onClick={() => {
                setAuthoring(true);
              }}
            >
              {t('contracts.new.open')}
            </button>
          ) : undefined
        }
      />

      <div className="lq-ct__filters">
        <input
          className="lq-ct__search"
          type="search"
          value={search}
          placeholder={t('contracts.search.placeholder')}
          aria-label={t('contracts.search.label')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <div
          className="lq-ct__filterGroup"
          role="group"
          aria-label={t('contracts.filter.label')}
        >
          {STATE_FILTERS.map((each) => (
            <button
              key={each}
              type="button"
              className="lq-ct__filter"
              aria-pressed={each === filter}
              onClick={() => {
                setFilter(each);
              }}
            >
              {t(`contracts.filter.${each}`)}
            </button>
          ))}
        </div>
        <span className="lq-ct__filterGap" />
        {mayEvaluate && active.length > 0 && (
          <button
            type="button"
            className="lq-sect__ghost"
            disabled={evaluate.isPending}
            onClick={evaluateAll}
          >
            {t('contracts.evaluateAll', { count: active.length })}
          </button>
        )}
      </div>

      {readOnly ? (
        <div className="lq-ct__readOnly">
          <strong>{t('contracts.readOnly.title')}</strong>
          <span>{t('contracts.readOnly.body')}</span>
        </div>
      ) : null}

      {truncated && (
        /* The bound, stated in the filter's own terms. Without it, "no contract in this state" is a
           claim about the tenant made from a page boundary — and this listing has no cursor, so
           `truncated` is the only signal there is. */
        <div className="lq-band lq-band--warn lq-band--closing">
          <div className="lq-band__title">{t('contracts.truncated.title')}</div>
          <div className="lq-band__body">
            {t('contracts.truncated.body', { count: all.length })}
          </div>
        </div>
      )}

      {message !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">
            {t(message.titleKey, message.values)}
          </strong>
          <span className="lq-flash__body">{t(message.bodyKey, message.values)}</span>
        </div>
      )}

      {contracts.isPending && (
        <div
          className="lq-ct__loading"
          role="status"
          aria-label={t('contracts.loading')}
        />
      )}

      {contracts.isError && (
        <Alert tone="error" title={t('contracts.error.title')}>
          {contracts.error.message}
        </Alert>
      )}

      {contracts.isSuccess &&
        (visible.length === 0 ? (
          /* Three sentences rather than two. An empty estate is a fact about the tenant; an empty
             filter is a fact about the filter; an empty filter over a *bounded* page is a fact
             about the page, and reporting it as either of the others would be the claim this
             screen exists to avoid making. */
          <div className="lq-cp__none">
            <div className="lq-cp__noneTitle">
              {t(
                filter === 'all' && needle === ''
                  ? 'contracts.empty.title'
                  : 'contracts.filtered.title',
              )}
            </div>
            <div className="lq-cp__noneBody">
              {t(
                filter === 'all' && needle === ''
                  ? 'contracts.empty.body'
                  : truncated
                    ? 'contracts.filtered.bounded'
                    : 'contracts.filtered.body',
              )}
            </div>
          </div>
        ) : (
          <div className="lq-ct__cards">
            {visible.map((contract) => (
              <ContractCard
                key={contract.id}
                contract={contract}
                datasetName={datasetNames.data?.byId.get(contract.dataset_id)}
                breach={breaches.get(contract.id)}
                maySign={maySign}
                signing={signing?.id === contract.id ? signing.side : null}
                signError={signError?.id === contract.id ? signError.message : undefined}
                onSign={(side) => {
                  signFor(contract, side);
                }}
              />
            ))}
          </div>
        ))}

      {authoring && (
        <NewContractDrawer
          onClose={() => {
            setAuthoring(false);
          }}
          onCreated={(contract) => {
            /* The confirmation lives on the screen rather than inside the form, which unmounts —
               and it names the next step, because a draft is not yet a promise anybody has been
               asked to keep. */
            setMessage({
              titleKey: 'contracts.acted.drafted',
              bodyKey: 'contracts.acted.draftedBody',
              values: { name: contract.name },
            });
          }}
        />
      )}
    </div>
  );
}
